import { useState, useCallback, useEffect } from 'react';
import { searchAssignees, CrmUser } from '../services/assigneeService';

const DEBOUNCE_DELAY = 300; // ms

/**
 * Custom hook for searching assignees with debouncing
 * Returns loading state, results, and a search function
 */
export const useAssigneeSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CrmUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Perform the actual search
  const performSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await searchAssignees(keyword);
      setResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search input with debouncing
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer
    if (!value.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(value);
    }, DEBOUNCE_DELAY);

    setDebounceTimer(timer);
  }, [debounceTimer, performSearch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return {
    searchTerm,
    results,
    loading,
    handleSearch,
  };
};
